import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Input, Modal, Switch } from '@cognitiv/cyprus-ui';
import { faFilterList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectJobStatesList } from 'ducks/job-states-list/selectors';
import { selectMaxResultsList } from 'ducks/max-results-list/selectors';
import { selectAdvancedFiltering } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { AdvancedFilteringQuery } from 'ducks/types/query-types';
import { selectUser } from 'ducks/user/selectors';
import { useOutsideClick } from 'hooks/events/useOutsideClick';
import { useQuerySave } from 'hooks/useQuerySave';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { AdvancedFilterButtons } from 'modals/advanced-filtering/components/AdvancedFilterButtons';
import { AdvancedFilterCalendar } from 'modals/advanced-filtering/components/AdvancedFilterCalendar';
import { AdvancedFilterResults } from 'modals/advanced-filtering/components/AdvancedFilterResults';
import { date_range, DateRange } from 'modals/advanced-filtering/constants';
import { useFilterContext } from 'modals/advanced-filtering/context/FilterContext';
import { useJobState } from 'modals/advanced-filtering/hooks/useJobState';
import { refresh_time_array, refresh_time_map } from 'modals/constants';
import moment from 'moment';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { offsetTimezoneUTC } from 'utils/dates';
import { handleError } from 'utils/errors';
import { getAdvFilteringTheme } from 'utils/modal/helpers';
import pushStates from 'utils/push-states';
import { parseQueries, replaceSearchQuery } from 'utils/queries';

import cn from 'modals/advanced-filtering/components/AdvancedFilter.module.scss';

interface ParamsProps {
  filter?: string;
  project_id?: number;
  job_state?: string;
}

export const AdvancedFilteringModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { theme, is_dark_theme } = useSwitchTheme();
  const params: ParamsProps = useParams();
  const wrapperRef = useRef(null);

  const job_states = useAppSelector(selectJobStatesList);
  const max_results = useAppSelector(selectMaxResultsList);
  const advanced_filtering = useAppSelector(selectAdvancedFiltering, shallowEqual);
  const user = useAppSelector(selectUser);

  useOutsideClick(wrapperRef, { advanced_filtering: false });
  const query: AdvancedFilteringQuery = parseQueries(search);

  const [dropdown_change, setDropDownChange] = useState(false);
  const [date_range_select, setDateRangeSelect] = useState<DateRange[number]>(date_range[0]);

  const { form, onJobPagesSelect, onRefreshClick, onDateChange, onChange } = useFilterContext();

  const {
    start_date,
    finish_date,
    created_by,
    include_archived,
    job_state,
    max_rows,
    parameter_name,
    parameter_value,
    range_name,
    refresh_timeout,
    min_refresh,
  } = form;

  const handleDropDownChange = useCallback((value: boolean) => {
    setDropDownChange(value);
  }, []);

  const { resetPageQueries } = useQuerySave({
    page: pathname.includes('jobs') ? 'jobs' : null,
    search,
  });
  const [, , , page] = pathname.split('/');

  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const { getJob } = useJobState(intervalRef, min_refresh, pathname, query, params.job_state, params.project_id);

  useEffect(() => {
    const interval = intervalRef.current;
    getJob();
    return () => clearInterval(interval);
  }, [min_refresh, params, query, getJob]);

  const clearFilter = useCallback(() => {
    const queries = resetPageQueries();
    setDateRangeSelect('Custom');
    navigate(`${pathname}${queries}`);
  }, [navigate, pathname, resetPageQueries]);

  useEffect(() => {
    onChange({
      range_name: query.range_name,
      start_date: query.start_date,
      finish_date: query.finish_date,
      parameter_name: query.parameter_name || null,
      parameter_value: query.parameter_value || null,
      max_rows: query.max_rows || 100,
      include_archived: query.include_archived || null,
      created_by: !!query.created_by,
      job_state: params.filter || null,
      refresh_timeout: query.refresh_timeout,
      min_refresh: query.refresh_timeout && refresh_time_map?.[query.refresh_timeout],
    });
  }, [
    onRefreshClick,
    onDateChange,
    params.filter,
    pathname,
    query.created_by,
    query.refresh_timeout,
    query.start_date,
    search,
    onChange,
    query.range_name,
    query.finish_date,
    query.parameter_name,
    query.parameter_value,
    query.max_rows,
    query.include_archived,
  ]);

  const handleModalClose = useCallback(() => {
    dispatch(setModal({ advanced_filtering: false }));
  }, [dispatch]);

  const parseDate = useCallback(
    (value: DateRange[number] | 'start_date' | 'finish_date', data?: string) => {
      const yesterday = moment().add(-1, 'days').format('L');
      const today = moment().format('L');
      const last_week = moment().subtract(1, 'weeks');
      const current_week = moment().startOf('week').format('L');
      const start_month = moment().startOf('month').format('L');

      switch (value) {
        case 'Today':
          onDateChange('start_date', `${today} 12:00 AM`);
          onDateChange('finish_date', `${today} 12:00 PM`);
          setDateRangeSelect(value);
          break;
        case 'Yesterday':
          onDateChange('start_date', `${yesterday} 12:00 AM`);
          onDateChange('finish_date', `${yesterday} 12:00 PM`);
          setDateRangeSelect(value);
          break;
        case 'Last Week':
          onDateChange('start_date', `${last_week.startOf('week').format('L')} 12:00 AM`);
          onDateChange('finish_date', `${last_week.endOf('week').format('L')} 12:00 PM`);
          setDateRangeSelect(value);
          break;
        case 'Current Week':
          onDateChange('start_date', `${current_week} 12:00 AM`);
          onDateChange('finish_date', `${today} 12:00 PM`);
          setDateRangeSelect(value);
          break;
        case 'Current Month':
          onDateChange('start_date', `${start_month} 12:00 AM`);
          onDateChange('finish_date', `${today} 12:00 PM`);
          setDateRangeSelect(value);
          break;
        case 'start_date':
        case 'finish_date':
          data && onDateChange(value, data);
          if (dropdown_change === true) {
            setDateRangeSelect(date_range[0]);
            setDropDownChange(false);
          }
          break;
        case 'Custom':
          setDateRangeSelect(date_range[0]);
          break;
      }
    },
    [dropdown_change, onDateChange],
  );

  const handleActivityFlag = useCallback(
    (checked: boolean) => {
      pushStates(location, navigate, { archived: checked });
    },
    [navigate],
  );

  const handleLockedFlag = useCallback(
    (checked: boolean) => {
      pushStates(location, navigate, { locked: checked });
    },
    [navigate],
  );

  const setFilter = useCallback(async () => {
    try {
      const { project_id } = params;
      const queries = replaceSearchQuery({
        start_date: offsetTimezoneUTC(start_date, { local_timezone: false }),
        finish_date: offsetTimezoneUTC(finish_date, { local_timezone: false }),
        range_name,
        max_rows,
        include_archived,
        created_by: created_by ? user.display_name : null,
        parameter_name,
        parameter_value,
        refresh_timeout: refresh_timeout,
        custom: true,
        filter: query.filter,
      });

      navigate(`/projects/${project_id}/${page}/${job_state || 'all'}${queries}`);
    } catch (err) {
      handleError(err);
    }
    handleModalClose();
  }, [
    created_by,
    refresh_timeout,
    finish_date,
    handleModalClose,
    include_archived,
    job_state,
    max_rows,
    navigate,
    page,
    parameter_name,
    parameter_value,
    params,
    query.filter,
    range_name,
    start_date,
    user.display_name,
  ]);

  const onJobFilterNameChange = useCallback(
    (value: { job_filter_name: string | null }) => {
      onChange({ job_state: value.job_filter_name });
    },
    [onChange],
  );

  const onParameterNameChange = useCallback(
    (value: string | null) => {
      onChange({ parameter_name: value });
    },
    [onChange],
  );

  const onParameterValueChange = useCallback(
    (value: string | null) => {
      onChange({ parameter_value: value });
    },
    [onChange],
  );

  const onCreatedChange = useCallback(
    (value: boolean) => {
      onChange({ created_by: value });
    },
    [onChange],
  );

  const onIncludeArchivedChange = useCallback(
    (value: boolean | null) => {
      onChange({ include_archived: value });
    },
    [onChange],
  );

  const switch_theme = useMemo(() => getAdvFilteringTheme(theme), [theme]);

  return (
    <div className={cn.advancedFiltering}>
      <Modal
        name="advanced_filtering"
        show={advanced_filtering}
        padding={0}
        width={450}
        onClose={handleModalClose}
        standard={false}
        close_box={20}
        animation
      >
        <div ref={wrapperRef} className={cn.filteringWrapper} style={{ backgroundColor: theme.element_background }}>
          <div className={cn.filtersWrapper}>
            <FontAwesomeIcon className={cn.filterIcon} icon={faFilterList} style={{ color: theme.font_color }} />
            <span className={cn.filterTitle} style={{ color: theme.font_color }}>
              Filters
            </span>
          </div>
          {location?.pathname.split('/')[1] !== 'admin' ? (
            <form className={cn.filterBody}>
              <div>
                <AdvancedFilterCalendar onDropDownChange={handleDropDownChange} onParseDate={parseDate} />
                <AdvancedFilterResults
                  date_range_select={date_range_select}
                  max_results={max_results}
                  onParseDate={parseDate}
                  onDropDownChange={handleDropDownChange}
                />
                {page === 'jobs' && (
                  <div>
                    <Dropdown
                      label="Job State"
                      options={job_states}
                      scroll_height={240}
                      value={job_state}
                      option_key="job_filter_name"
                      margin="24px 0px 0px 0px"
                      onSelect={onJobFilterNameChange}
                    />
                  </div>
                )}
                <div className={cn.parameter}>
                  <Input
                    label="Parameter"
                    width="calc(50% - 10px)"
                    margin="12px 0px 0px 0px"
                    placeholder="Name"
                    value={parameter_name ?? ''}
                    onChange={onParameterNameChange}
                  />
                  <div className={cn.parameterValue}>=</div>
                  <Input
                    margin="28px 0px 0px 0px"
                    width="calc(50% - 10px)"
                    placeholder="Value"
                    value={parameter_value ?? ''}
                    onChange={onParameterValueChange}
                  />
                </div>
                <div className={cn.flexAdvFiltering}>
                  <Switch
                    onClick={onCreatedChange}
                    checked={created_by || false}
                    width="50%"
                    margin="5px 0px 0px 0px"
                    theme={switch_theme}
                    append_label="Created By Me"
                  >
                    Created By Me
                  </Switch>
                  {(page === 'job_definitions' || page === 'batch_definitions') && (
                    <Switch
                      onClick={onIncludeArchivedChange}
                      checked={include_archived || false}
                      width="calc(50%-10px)"
                      margin="5px 0px 0px 10px"
                      theme={switch_theme}
                      append_label="Include Archived"
                    >
                      Include Archived
                    </Switch>
                  )}
                </div>
                {page === 'jobs' && (
                  <>
                    <Switch
                      theme={switch_theme}
                      onClick={onRefreshClick}
                      checked={refresh_timeout || false}
                      width="50%"
                      margin="0px 0px 0px 0px"
                      append_label="Auto-Refresh"
                    />
                    <div
                      className={classNames({
                        [cn.jobsVisible]: refresh_timeout,
                        [cn.jobsHidden]: !refresh_timeout,
                      })}
                    >
                      <div>
                        <h3 className={cn.filterResults} style={{ color: theme.font_color }}>
                          Occurring every
                        </h3>
                        <div className={cn.resultsFlex}>
                          {refresh_time_array.map((el, id) => (
                            <div
                              key={el.name}
                              onClick={() => onJobPagesSelect(el)}
                              className={classNames(is_dark_theme ? cn.resultDark : cn.result, {
                                [cn.active]: refresh_timeout === el.name,
                                [cn.resultMarginLeft]: id !== 0,
                              })}
                              style={{ color: theme.font_color }}
                            >
                              {el.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <AdvancedFilterButtons onSetFilter={setFilter} onClearFilter={clearFilter} />
              </div>
            </form>
          ) : (
            <div className={cn.advSwitchButtons}>
              <Switch
                theme={switch_theme}
                onClick={handleLockedFlag}
                checked={query.locked}
                width="50%"
                margin="24px 0px 0px 0px"
                append_label="Highlight Locked"
              />
              <Switch
                theme={switch_theme}
                onClick={handleActivityFlag}
                checked={query.archived}
                width="50%"
                margin="24px 0px 0px 0px"
                append_label="Show Archived"
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
